import type { Answer, Question } from '@all-for-one/shared-types'
import React from 'react'
import { sectionLabel } from '@all-for-one/shared-types'
import styles from 'css/pages/student-live.module.css'
import { MultipleChoiceAnswering } from 'lesson-builder/question-types/multiple-choice'
import { ShortAnswerAnswering } from 'lesson-builder/question-types/short-answer'
import { FillBlankAnswering } from 'lesson-builder/question-types/fill-blank'
import { NextQuestionButton, DoneButton } from 'components/blue-button'
import { reportError } from 'util/error-reporting'
import { useSpeech, ReadAloudButton } from 'components/speech'
import { questionSpeechText } from 'components/question-speech-text'

const QUESTION_COMPONENTS = {
	'multiple-choice': MultipleChoiceAnswering,
	'short-answer': ShortAnswerAnswering,
	'fill-blank': FillBlankAnswering
} as const;

interface StudentQuestionViewProps {
	question: Question
	currentIndex: number
	totalQuestions: number
	learningStage: string | null
	answer: Answer | undefined
	speech: ReturnType<typeof useSpeech>
	ensureInitializedAnswer: (q: Question) => void
	onAnswerChange: (questionId: string, value: string | string[]) => void
	onAdvance: () => void
	onDone: () => void
}

/**
 * Renders the student's answering UI for one delivered question: per-type
 * component dispatch, the unknown-question-type fallback (err-13 — an
 * AI-generated question with an unrecognized type used to crash the whole
 * render tree), fill-blank answer reconstruction, and the read-aloud /
 * next-or-done controls.
 */
export const StudentQuestionView = ({
	question: q,
	currentIndex,
	totalQuestions,
	learningStage,
	answer: currentAnswer,
	speech,
	ensureInitializedAnswer,
	onAnswerChange,
	onAdvance,
	onDone,
}: StudentQuestionViewProps) => {
	const QuestionComponent = QUESTION_COMPONENTS[q.type] as any
	// err-13: an AI-generated question with an unknown type used to
	// crash the whole render tree. Show a fallback and let the
	// student keep moving; the blank answer grades as 0.
	if (!QuestionComponent) {
		reportError('manual', new Error(`unknown question type: ${String((q as any).type)}`), { where: 'student-live render' })
		return (
			<div className={styles.questionContent} style={{ textAlign: 'center' }}>
				<h2>This question can't be shown</h2>
				<p style={{ fontSize: '18px' }}>Skip it and keep going — tell your teacher about this one.</p>
				<div className={styles.questionActions}>
					{currentIndex < totalQuestions - 1
						? <NextQuestionButton onClick={onAdvance} />
						: <DoneButton onClick={onDone} />}
				</div>
			</div>
		)
	}

	let answerValue: any = undefined
	if (q.type === 'fill-blank') {
		const expected = (q as any).question.length - 1
		if (currentAnswer?.studentAnswer) {
			const parts = String(currentAnswer.studentAnswer).split(' ')
			answerValue = Array.from({ length: expected }, (_, i) => parts[i] || '')
		}
	} else {
		answerValue = currentAnswer?.studentAnswer ?? ''
	}
	ensureInitializedAnswer(q)

	return (
		<div className={styles.questionContent}>
			{learningStage ? (<h2>{`${sectionLabel(learningStage)} Questions`}</h2>) : null}
			<div className={styles.readAloudRow}>
				<ReadAloudButton speech={speech} getText={() => questionSpeechText(q)} />
			</div>
			<QuestionComponent
				key={q.id}
				question={q}
				answer={answerValue}
				onAnswerChange={(val: any) => onAnswerChange(q.id, val)}
			/>
			<div className={styles.questionActions}>
				{currentIndex < totalQuestions - 1 ? (
					<NextQuestionButton onClick={() => { ensureInitializedAnswer(q); onAdvance() }} />
				) : (
					<DoneButton onClick={onDone} />
				)}
			</div>
		</div>
	)
}
